/**
 * ffmpeg-pool public surface + the gated run wrapper.
 *
 * **Disabled by default.** The host opts in via {@link configureFfmpegPool}:
 * apps/api wires it to a knob; the CLI never configures it, so it stays off.
 * When disabled, {@link runFfmpeg} executes the caller's inline fallback (today's
 * raw `spawn('ffmpeg')`) — so flipping the gate is the only thing that changes
 * execution.
 *
 * This is the reusable ffmpeg API: every ffmpeg/ffprobe call site speaks the
 * {@link FfmpegRunSpec}/{@link FfmpegRunResult} pair through {@link runFfmpeg},
 * gaining pooling + a wall-clock/RSS-bounded child without re-rolling spawn/kill.
 *
 * @module ffmpeg-pool
 */

import { cpus } from 'node:os';
import { type BrowserLogger, consoleLogger } from '../logger.js';
import { withExpensiveSlot } from '../expensive-gate.js';
import { recordInlineFallback } from '../worker-pool/inline-fallback.js';
import {
  FfmpegPool,
  type FfmpegPoolOptions,
  type FfmpegPoolStats,
  FfmpegWorkerError,
} from './pool.js';
import type { FfmpegRunResult, FfmpegRunSpec } from './protocol.js';

export {
  FfmpegPool,
  FfmpegWorkerError,
  resolveFfmpegRunnerPath,
  type FfmpegPoolOptions,
  type FfmpegPoolStats,
} from './pool.js';
export type {
  FfmpegBin,
  FfmpegOutcome,
  FfmpegRunResult,
  FfmpegRunSpec,
  StderrCapture,
} from './protocol.js';

/** Default per-call wall-clock budget for an ffmpeg/ffprobe run (runner-enforced). */
export const DEFAULT_FFMPEG_TIMEOUT_MS = 300_000;

/**
 * Fallback runner count when the host doesn't pass `size`. The real concurrency
 * bound is the SHARED expensive gate (`worker.expensive.max-concurrency`), not
 * this number — the pool just needs enough runner processes to reach that cap, so
 * the host sizes it from the same coefficient. Bounded to [1, 4].
 *
 * ⚠️ `os.cpus()` reports the HOST's cores, not the container's CPU limit — only a
 * last-resort default; prefer the host-passed size.
 */
const defaultFfmpegPoolSize = (): number => Math.min(4, Math.max(1, Math.floor(cpus().length / 4)));

export interface FfmpegPoolConfig {
  /** Master gate. `boolean` or a function so the host can back it with a live knob. */
  enabled: boolean | (() => boolean);
  /** Runner count. Applied when the pool is first created. */
  size?: number;
  /** Default per-call SIGKILL budget for the ffmpeg child. */
  timeoutMs?: number;
  /** Per-child RSS cap (bytes). Unset/0 ⇒ the runner's RSS sampler is off. */
  rssBytes?: number;
  /**
   * Arm the per-driver CPU calibration sampler at this cadence (ms). Unset/`0` ⇒ off.
   * Applied **live** via {@link configureFfmpegPool} — starts/stops on a warm pool.
   */
  measureResourcesMs?: number;
  logger?: BrowserLogger;
  /** @internal test seam — override the runner entry (see {@link FfmpegPoolOptions.runnerPath}). */
  runnerPath?: string;
  /** @internal test seam — inject spawn (see {@link FfmpegPoolOptions.spawn}). */
  spawn?: FfmpegPoolOptions['spawn'];
}

let config: FfmpegPoolConfig = { enabled: false };
let pool: FfmpegPool | null = null;

/**
 * Configure (and enable/disable) the ffmpeg pool. Safe to call repeatedly — e.g.
 * on knob change. A literal `enabled: false` tears the pool down; the API passes a
 * function gate, so it owns teardown via {@link shutdownFfmpegPool}.
 */
export function configureFfmpegPool(next: FfmpegPoolConfig): void {
  config = next;
  if (typeof next.enabled === 'boolean' && next.enabled === false) {
    void shutdownFfmpegPool();
    return;
  }
  // Apply the calibration cadence live (idempotent; no-op until the pool is built).
  pool?.setMeasureResourcesMs(config.measureResourcesMs ?? 0);
}

export function isFfmpegPoolEnabled(): boolean {
  const { enabled } = config;
  return typeof enabled === 'function' ? !!enabled() : enabled;
}

function getPool(): FfmpegPool {
  if (!pool) {
    pool = new FfmpegPool({
      size: config.size ?? defaultFfmpegPoolSize(),
      defaultTimeoutMs: config.timeoutMs ?? DEFAULT_FFMPEG_TIMEOUT_MS,
      ...(config.rssBytes ? { rssBytes: config.rssBytes } : {}),
      ...(config.measureResourcesMs ? { measureResourcesMs: config.measureResourcesMs } : {}),
      ...(config.runnerPath ? { runnerPath: config.runnerPath } : {}),
      ...(config.spawn ? { spawn: config.spawn } : {}),
      logger: config.logger ?? consoleLogger,
    });
  }
  return pool;
}

export async function shutdownFfmpegPool(): Promise<void> {
  const p = pool;
  pool = null;
  if (p) await p.shutdown();
}

/**
 * Current pool stats, or `null` when the pool isn't built (disabled / torn down).
 * The host's telemetry loop reads this each interval and emits per-driver metrics.
 */
export function getFfmpegPoolStats(): FfmpegPoolStats | null {
  return pool ? pool.stats() : null;
}

/** @internal test seam — also reclaims any live runner processes. */
export function _resetFfmpegPoolForTest(): void {
  const p = pool;
  config = { enabled: false };
  pool = null;
  if (p) void p.shutdown();
}

/**
 * Run one ffmpeg/ffprobe, pooled when enabled and otherwise via the caller's
 * inline fallback (today's raw spawn). The fallback returns the same
 * {@link FfmpegRunResult} shape, so callers get a uniform result either way.
 *
 * A runner/pool FAULT (crash, backstop timeout, spawn failure) falls back inline
 * — the work itself didn't breach its limit, so running it on the host is safe. An
 * ffmpeg-child breach (`outcome: 'timeout' | 'oom'`) is returned to the caller,
 * not retried inline (re-running a pathological input is exactly what the bound
 * exists to prevent).
 *
 * Every inline fallback taken for a *fault* is counted
 * ({@link recordInlineFallback}) so a systemic degradation — where every routed
 * site drops off the shared gate at once — is visible instead of silent. The
 * pool-disabled path is the configured behavior, not a fault, and is not counted.
 */
export async function runFfmpeg(
  spec: FfmpegRunSpec,
  inlineFallback: () => Promise<FfmpegRunResult>
): Promise<FfmpegRunResult> {
  if (!isFfmpegPoolEnabled()) {
    return inlineFallback();
  }
  const logger = config.logger ?? consoleLogger;
  // Probe construction BEFORE queueing for a gate slot: resolving the runner entry
  // can fail, and that failure is systemic (no call can ever be pooled) rather than
  // a per-call runner fault — it deserves its own reason and shouldn't cost a slot.
  // The pool is still resolved late inside the closure below, so a teardown during
  // a long gate wait transparently rebuilds instead of failing this call.
  try {
    getPool();
  } catch (err) {
    recordInlineFallback('ffmpeg', 'pool_unavailable');
    logger.warn('[FfmpegPool] pool unavailable; running ffmpeg inline', {
      bin: spec.bin ?? 'ffmpeg',
      error: err instanceof Error ? err.message : String(err),
    });
    return inlineFallback();
  }
  try {
    // Draw from the SHARED expensive budget (worker.expensive.max-concurrency) so
    // ffmpeg encodes and snapshot format/augment can't jointly oversubscribe the
    // worker — released before any inline fallback below.
    return await withExpensiveSlot(() => getPool().run(spec));
  } catch (err) {
    if (err instanceof FfmpegWorkerError) {
      recordInlineFallback('ffmpeg', 'runner_fault');
      logger.warn('[FfmpegPool] runner fault; running ffmpeg inline', {
        bin: spec.bin ?? 'ffmpeg',
        error: err.message,
      });
      return inlineFallback();
    }
    throw err;
  }
}

/** Default budget for the boot smoke-test round-trip (spawn + handshake + `ffmpeg -version`). */
const SMOKE_TEST_TIMEOUT_MS = 5_000;

/**
 * Boot smoke-test: spawn one runner and round-trip `ffmpeg -version` end-to-end.
 *
 * This converges the two spawn-and-verify idioms — cpu-offload's
 * `smokeTestCpuOffload` (spawn a child + round-trip a probe) and the old
 * `isFfmpegAvailable` (spawn `ffmpeg -version`, confirm exit 0) — into one check:
 * it proves the runner can SPAWN + handshake AND that ffmpeg actually runs. Throws
 * on any failure so the host logs a loud boot failure instead of silently falling
 * back inline per encode. Call AFTER {@link configureFfmpegPool}.
 */
export async function smokeTestFfmpegPool(timeoutMs: number = SMOKE_TEST_TIMEOUT_MS): Promise<void> {
  const result = await getPool().run({ bin: 'ffmpeg', args: ['-version'], stderr: false, timeoutMs });
  if (result.outcome !== 'exit' || result.code !== 0) {
    throw new FfmpegWorkerError(
      `ffmpeg smoke-test failed: outcome=${result.outcome} code=${result.code}`
    );
  }
}
